import React from 'react';
import { AlertCircle } from 'lucide-react';
import { SNAPSHOT_REQUIRED_MESSAGE } from '@/pages/analytics/overview/chartRenderers';

export interface ChangeFeedItem {
  id: string;
  text: string;
  href?: string;
}

export interface ChangeFeedPanelProps {
  items: ChangeFeedItem[];
  onNavigate: (href: string) => void;
  /** True when the project only has one snapshot (cannot compute deltas). */
  singleSnapshot?: boolean;
}

/**
 * "What changed" mini-panel for the Analytics Overview cockpit.
 * Shows 3–6 bullet items linking to focus charts or filtered Activities.
 * When only one snapshot exists, shows an explanatory empty state.
 */
export const ChangeFeedPanel: React.FC<ChangeFeedPanelProps> = ({
  items,
  onNavigate,
  singleSnapshot = false,
}) => {
  if (singleSnapshot || items.length === 0) {
    return (
      <div className="border rounded-lg bg-card p-4">
        <h3 className="text-sm font-medium text-foreground mb-2">What changed</h3>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>
            {singleSnapshot
              ? SNAPSHOT_REQUIRED_MESSAGE
              : 'No notable changes detected.'}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="border rounded-lg bg-card p-4">
      <h3 className="text-sm font-medium text-foreground mb-2">What changed</h3>
      <ul className="space-y-1.5">
        {items.slice(0, 6).map((item) => (
          <li key={item.id} className="text-xs text-muted-foreground flex items-start gap-2">
            <span className="shrink-0 mt-0.5 h-1.5 w-1.5 rounded-full bg-primary" />
            {item.href ? (
              <button
                type="button"
                className="text-left hover:text-foreground hover:underline transition-colors"
                onClick={() => onNavigate(item.href!)}
              >
                {item.text}
              </button>
            ) : (
              <span>{item.text}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};
